import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Select, { MultiValue, components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { Search, FileText, Download } from "lucide-react";
import { showError, showSuccess } from "toast/toast";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

// ===== Types =====
import { getAuthCookie } from "../../utils/authCookies";
// Group payer scope hidden — keep type narrow to office | agent only
type PayerScope = "office" | "agent";
type Office = { id: string; label: string };
type Agent = { id: string; name: string };
// type Group = { id: string; name: string };
type TeamMember = { id: string; name: string; firstName: string; lastName: string };
type Vendor = { supplier_id: string; name: string; email: string; linked_at: string };

type TeamMemberOption = { 
  value: string; 
  label: string; 
  raw: { id: string; name: string } // Select2 format for team members
};

type OfficeOption = { 
  value: string; 
  label: string; 
  raw: { id: string; name: string } // Select2 format for offices
};

type AgentOption = { 
  value: string; 
  label: string; 
  raw: { id: string; name: string } // Select2 format for agents
};

// Select2 type for brokerage agents
type BrokerageAgentOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

// ===== React Select Components =====
const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
    borderWidth: 2,
    borderColor: "#c9ced6",
    minHeight: 44,
    borderRadius: 8,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any) => ({ ...p, padding: "6px 10px" }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValue: (p: any, s: any) => ({ ...p, backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor }),
  multiValueLabel: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValueRemove: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : p.display }),
};

interface BrokeragePayerScopeSectionProps {
  onOrdersFetched?: (data: {
    orders: any[];
    total: number;
    totalPages: number;
    page: number;
    limit: number;
  }) => void;
  onVendorChange?: (vendor: Vendor | null) => void;
  onScopeDataChange?: (scopeData: {
    scope: string;
    company: string;
    teamMembers: string[];
    offices: string[];
    agents: string[];
    bill_from: string;
    bill_to: string;
  }) => void;
  pagination?: { pageIndex: number; pageSize: number };
  onPaginationReset?: () => void;
  /** Selected orders from PaymentOrders — sent only when non-empty */
  selectedOrdersForReport?: Array<{ order_id: string; order_type: string }>;
}

export default function BrokeragePayerScopeSection({ onOrdersFetched, onVendorChange, onScopeDataChange, pagination: externalPagination, onPaginationReset, selectedOrdersForReport = [] }: BrokeragePayerScopeSectionProps) {
  // Auth placeholders (replace with real context)
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const userName = getAuthCookie("userName");
  const navigate = useNavigate();

  // Detect login type
  const location = useLocation();
  const isAgent = location.pathname.includes('/agent');
  const isTeammember = location.pathname.includes('/teammember');
  const isRealtor = location.pathname.includes('/realtor');

  // State
  const [scope, setScope] = useState<PayerScope>(`${isAgent ? "agent" : "office"}`);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedOffices, setSelectedOffices] = useState<Office[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<any>(null);
  const [selectedOfficeOption, setSelectedOfficeOption] = useState<OfficeOption | null>(null);

  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [selectedAgentOption, setSelectedAgentOption] = useState<AgentOption | null>(null);
  
  // State for AsyncPaginate brokerage agents
  const [selectedBrokerageAgentOption, setSelectedBrokerageAgentOption] = useState<BrokerageAgentOption | null>(null);

  // State for parent brokerage firm (for teammember login)
  const [parentBrokerageId, setParentBrokerageId] = useState<string | null>(null);
  const [parentBrokerageName, setParentBrokerageName] = useState<string | null>(null);

  // const [groups, setGroups] = useState<Group[]>([]);
  // const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<TeamMember[]>([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState<any>(null);
  const [selectedTeamMemberOption, setSelectedTeamMemberOption] = useState<TeamMemberOption | null>(null);

  const [billToCompany, setBillToCompany] = useState(true);
  const [placedBy, setPlacedBy] = useState({ officeManager: true, agent: true });
  const [orderTypes, setOrderTypes] = useState({ sales: true, service_install: true, removal: true });
  const [dateField, setDateField] = useState("Due Date");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusUnpaid, setStatusUnpaid] = useState(true);
  const [statusPartial, setStatusPartial] = useState(true);
  const [statusOverdue, setStatusOverdue] = useState(true);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingOrders, setFetchingOrders] = useState(false);
  const [ordersFetched, setOrdersFetched] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  // ===== API: Team Members with Select2 =====
  const PAGE_SIZE = 10;
  
  // Helper function for AsyncPaginate
  const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");

  // Fetch parent hierarchy for teammember login
  const fetchParentHierarchy = async () => {
    if (!isTeammember || !token) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Find the parent with license_type "BROKERAGE_FIRM"
        const brokerageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "BROKERAGE_FIRM"
        );
        
        if (brokerageFirm) {
          setParentBrokerageId(brokerageFirm.id || brokerageFirm._id);
          setParentBrokerageName(brokerageFirm.name);
        }
      }
    } catch (error) {
      console.error("Error fetching parent hierarchy:", error);
    }
  };
  
  // ===== Brokerage Agent Loader =====
  const loadBrokerageAgentOptions: LoadOptions<BrokerageAgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      console.log("BrokerageAgentLoader firing with:", { page, inputValue });

      try {
        const params = new URLSearchParams({
          brokerageFirmID: (isTeammember && parentBrokerageId ? parentBrokerageId : userId) || "",
          licenseType: "BROKERAGE_AGENT",
          page: String(page),
          limit: String(PAGE_SIZE)
        });
        const q = (inputValue || "").trim();
        if (q) params.set("search", q);

        const url = `${process.env.REACT_APP_API_URL}/users-search?${params.toString()}`;


        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          console.error("Failed to fetch brokerage agents:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();
        console.log('Brokerage agent API response:', { page, q, json });

        const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

        const options: BrokerageAgentOption[] = list.map((u: any) => ({
          value: normalizeId(u),
          label: u?.text || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: u?.phone1 || "",
        }));

        const hasMore = Array.isArray(json)
          ? options.length === PAGE_SIZE
          : json?.pagination?.more ??
            (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        console.error("Failed to load brokerage agent options:", err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [userId, isTeammember, parentBrokerageId, token]
  );

  /** GET /offices/team-members/select2 — officeIDs[], optional q, page/limit; returns data.results + data.pagination.more */
  const fetchOfficeTeamMembersSelect2Page = useCallback(
    async (officeIds: string[], page: number, q: string) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (q) params.set("q", q);
      officeIds.forEach((id) => params.append("officeIDs[]", id));
      const url = `${process.env.REACT_APP_API_URL}/offices/team-members/select2?${params.toString()}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        return { results: [] as any[], hasMore: false };
      }
      const json = await response.json();
      const data = json?.data ?? json;
      const results = Array.isArray(data?.results) ? data.results : [];
      const hasMore = Boolean(data?.pagination?.more ?? false);
      return { results, hasMore };
    },
    [token]
  );

  const loadTeamMemberOptions: LoadOptions<TeamMemberOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();

      try {
        const officeIds = selectedOffices.map((o) => o.id).filter((id) => id && id !== "__ALL__");

        if (officeIds.length === 0) {
          // No office selected: brokerage-wide team members
          const brokerageId = (isTeammember && parentBrokerageId) ? parentBrokerageId : userId;
          const brokerageIds = brokerageId ? [brokerageId] : [];
          if (brokerageIds.length === 0) {
            return { options: [], hasMore: false, additional: { page } };
          }

          const params = new URLSearchParams({
            page: String(page),
            limit: String(PAGE_SIZE),
          });
          brokerageIds.forEach((id) => params.append("brokerageFirmIDs[]", id));
          if (q) params.set("q", q);
          const url = `${process.env.REACT_APP_API_URL}/users/brokerage-team-members/by-brokerage-ids/select2?${params.toString()}`;
          const response = await fetch(url, {
            headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
          });
          if (!response.ok) {
            return { options: [], hasMore: false, additional: { page } };
          }
          const data = await response.json();
          const options: TeamMemberOption[] = (data.results || []).map((item: any) => ({
            value: item.id || item._id,
            label: item.name,
            raw: item,
          }));
          const allOptions: TeamMemberOption[] =
            page === 1 && !q
              ? [{ value: "__ALL__", label: "All", raw: { id: "__ALL__", name: "All" } }]
              : [];
          return {
            options: [...allOptions, ...options],
            hasMore: data.pagination?.more || false,
            additional: { page: page + 1 },
          };
        }

        const { results, hasMore } = await fetchOfficeTeamMembersSelect2Page(officeIds, page, q);
        const options: TeamMemberOption[] = results.map((u: any) => {
          const id = String(u?.id ?? u?._id ?? "");
          const label = String(u?.text ?? u?.name ?? "").trim() || "---";
          return { value: id, label, raw: { id, name: label } };
        });
        const allOptions: TeamMemberOption[] =
          page === 1 && !q
            ? [{ value: "__ALL__", label: "All", raw: { id: "__ALL__", name: "All" } }]
            : [];
        return {
          options: [...allOptions, ...options],
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        console.error("Failed to load team member options:", err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedOffices, userId, isTeammember, parentBrokerageId, token, fetchOfficeTeamMembersSelect2Page]
  );

  // ===== API: Offices with Select2 =====
  const loadOfficeOptions: LoadOptions<OfficeOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // Determine brokerage id (use parentBrokerageId for teammember, userId otherwise)
      const brokerageId = (isTeammember && parentBrokerageId) ? parentBrokerageId : userId;
      if (!brokerageId) return { options: [], hasMore: false, additional: { page } };

      try {
        const q = (inputValue || "").trim();
        const url = `${process.env.REACT_APP_API_URL}/office/select/${brokerageId}`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ page, limit: PAGE_SIZE, search: q }),
        });
        if (!response.ok) return { options: [], hasMore: false, additional: { page } };
        const json = await response.json();
        const list = json?.results || [];
        const options: OfficeOption[] = list.map((office: any) => ({
          value: office.id || office._id,
          label: office.officeName || office.name || "---",
          raw: office,
        }));
        const allOptions: OfficeOption[] = page === 1 && !q ? [{ value: "__ALL__", label: "All", raw: { id: "__ALL__", name: "All" } }] : [];
        const hasMore = json?.pagination?.more ?? false;
        return { options: [...allOptions, ...options], hasMore, additional: { page: page + 1 } };
      } catch (err) {
        console.error("Failed to load office options:", err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [userId, isTeammember, parentBrokerageId, token]
  );

  // ===== API: Office-based Agents with Select2 =====
  const loadOfficeAgentOptions: LoadOptions<AgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      try {
        // Get selected office IDs from selectedOffices
        const officeIds = selectedOffices.map(office => office.id);
        
        if (officeIds.length === 0) {
          return { options: [], hasMore: false };
        }

        // Use the /agents/offices endpoint as specified
        const url = `${process.env.REACT_APP_API_URL}/agents/offices`;
        console.log('Loading office agent options:', { page, officeIds, url });
        
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            office_ids: officeIds
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Office agent options response:', data);

        // Filter by search term if provided
        const q = (inputValue || "").trim().toLowerCase();
        let filteredData = Array.isArray(data) ? data : [];
        
        if (q) {
          filteredData = filteredData.filter((agent: any) => {
            const name = `${agent.firstName || ""} ${agent.lastName || ""}`.trim().toLowerCase();
            return name.includes(q);
          });
        }

        // Apply pagination manually since this endpoint doesn't support it
        const startIndex = (page - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        const options: AgentOption[] = paginatedData.map((item: any) => ({
          value: item.id || item._id,
          label: `${item.firstName || ""} ${item.lastName || ""}`.trim() || "(no name)",
          raw: item
        }));

        // Add "All" option at the top only on first page and when not searching
        const allOptions: AgentOption[] = page === 1 && !q ? [{
          value: "__ALL__",
          label: "All",
          raw: { id: "__ALL__", name: "All" }
        }] : [];

        return {
          options: [...allOptions, ...options],
          hasMore: endIndex < filteredData.length,
          additional: { page: page + 1 }
        };
      } catch (err) {
        console.error("Failed to load office agent options:", err);
        return { options: [], hasMore: false };
      }
    },
    [selectedOffices, token]
  );

  // ===== API: Vendors =====
  const fetchSuppliersByUserId = async (userIdParam: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/agent/suppliers/${userIdParam}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch supplier list");
      }

      const data = await response.json();
      setVendors(data || []);
    } catch (err) {
      showError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  // ===== API: Offices by User =====
  // const fetchOffices = async (userIdParam: string) => {
  //   try {
  //     setLoading(true);
  //     const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  //     const resp = await fetch(`${base}/office/${userIdParam}`, { headers: { Authorization: `Bearer ${token}` } });
  //     const data = await resp.json();
  //     if (!resp.ok) showError(data?.error || "Failed to fetch offices");
  //     const list: Office[] = (data || []).map((o: any) => ({ id: o._id, label: o.officeName }));
  //     setOffices(list);
  //   } catch (e: any) {
  //     showError(e?.message || "Failed to fetch offices");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Removed fetchAgents function - now using AsyncPaginate for brokerage agents

  // const fetchGroups = async () => {
  //   try {
  //     setLoading(true);
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/customer-groups`,
  //       {
  //         method: "GET",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error("Failed to fetch groups");
  //     }

  //     const data = await response.json();
  //     const groupList: Group[] = (Array.isArray(data) ? data : []).map((group: any) => ({
  //       id: group._id || group.id,
  //       name: group.name || "",
  //     }));
  //     setGroups(groupList);
  //   } catch (err) {
  //     showError(err instanceof Error ? err.message : "Failed to fetch groups");
  //   } finally {
  //     setLoading(false);
  //   }
  // };


  useEffect(() => {
    // For teammember: wait for parentBrokerageId, use it when available
    // For realtor: use userId immediately
    // For agent: use userId for vendors only
    
    if (isTeammember) {
      // For teammember, only proceed if parentBrokerageId is available
      if (parentBrokerageId) {
        // Fetch vendors using parentBrokerageId
        fetchSuppliersByUserId(parentBrokerageId);
        // Fetch offices using parentBrokerageId
        // fetchOffices(parentBrokerageId);
      }
    } else if (!isAgent && userId) {
      // For realtor, use userId
      fetchSuppliersByUserId(userId);
      // fetchOffices(userId);
    } else if (isAgent && userId) {
      // For agent, only fetch vendors
      fetchSuppliersByUserId(userId);
    }
  }, [userId, isAgent, isTeammember, parentBrokerageId]);

  // Fetch parent hierarchy for teammember login
  useEffect(() => {
    if (isTeammember && token) {
      fetchParentHierarchy();
    }
  }, [isTeammember, token]);

  // Handler for scope change - clears all selections
  const handleScopeChange = (newScope: PayerScope) => {
    setScope(newScope);
    // Clear all selections when scope changes
    setSelectedTeamMembers([]);
    setSelectedTeamMemberOption(null);
    setSelectedOffices([]);
    setSelectedOfficeOption(null);
    setSelectedAgents([]);
    setSelectedAgentOption(null);
    setSelectedBrokerageAgentOption(null);
    // setSelectedGroup(null);
    setSelectedAgent(null);
    setSelectedOffice(null);
    // Clear orders fetched flag to hide payment components
    setOrdersFetched(false);
    // Notify parent to clear orders data
    if (onOrdersFetched) {
      onOrdersFetched({ orders: [], total: 0, totalPages: 0, page: 1, limit: 10 });
    }
  };

  // Notify parent component when scope data changes
  useEffect(() => {
    if (onScopeDataChange) {
      // For agent login, include the logged-in agent in the agents array
      const agentsList = isAgent && userName 
        ? [userName, ...selectedAgents.map(a => a.name)]
        : selectedAgents.map(a => a.name);
      
      const scopeData = {
        scope: scope,
        company: selectedVendor?.name || "",
        teamMembers: selectedTeamMembers.map(tm => tm.name),
        offices: selectedOffices.map(o => o.label),
        agents: agentsList,
        bill_from: selectedVendor?.name || "",
        bill_to: isAgent ? userName || "" : selectedVendor?.name || ""
      };
      onScopeDataChange(scopeData);
    }
  }, [scope, selectedVendor, selectedTeamMembers, selectedOffices, selectedAgents, isAgent, userName, onScopeDataChange]);

  // Removed fetchAgents useEffect - now using AsyncPaginate for brokerage agents

  // Fetch groups when scope changes to "group" (only for realtor/teammember) — hidden
  // useEffect(() => {
  //   if (!isAgent && scope === "group") {
  //     fetchGroups();
  //   }
  // }, [scope, isAgent]);


  // Helper function to fetch all offices
  const fetchAllOffices = async () => {
    try {
      const allOffices: Office[] = [];
      const teamMemberIds = selectedTeamMembers.map(tm => tm.id);

      // If team members are selected, use that endpoint to find their offices.
      if (teamMemberIds.length > 0) {
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const params = new URLSearchParams({
            page: String(page),
            limit: String(PAGE_SIZE)
          });
          teamMemberIds.forEach(id => params.append('teamMemberIDs[]', id));
          const url = `${process.env.REACT_APP_API_URL}/users/brokerage-team-members/offices/select2?${params.toString()}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          if (!response.ok) break;
          const data = await response.json();
          const results = data.results || [];
          results.forEach((item: any) => {
            allOffices.push({
              id: item.id || item._id,
              label: item.name
            });
          });
          hasMore = data.pagination?.more || false;
          page++;
        }
      } else {
        // Fallback: fetch all offices for the brokerage
        const brokerageId = (isTeammember && parentBrokerageId) ? parentBrokerageId : userId;
        if (brokerageId) {
          try {
            const url = `${process.env.REACT_APP_API_URL}/office/select/${brokerageId}`;
            const resp = await fetch(url, {
              method: "POST",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ page: 1, limit: 1000, search: "" }),
            });
            if (resp.ok) {
              const json = await resp.json();
              const list = json?.results || [];
              list.forEach((o: any) => {
                allOffices.push({ id: o.id || o._id, label: o.officeName || o.name || o.officeName || "---" });
              });
            }
          } catch (e) {
            console.error("Failed to fetch offices for brokerage:", e);
          }
        }
      }

      setSelectedOffices(allOffices);
    } catch (err) {
      console.error("Failed to fetch all offices:", err);
    }
  };

  // ===== Handlers for adding offices and agents =====
  const handleOfficeChange = (opt: OfficeOption | null) => {
    if (opt?.value) {
      if (opt.value === "__ALL__") {
        // Fetch and select all offices
        fetchAllOffices();
      } else {
        const office = { id: opt.value, label: opt.label };
        if (!selectedOffices.some(o => o.id === office.id)) {
          setSelectedOffices([...selectedOffices, office]);
        }
      }
    }
    // Clear the select after adding
    setSelectedOfficeOption(null);
  };

  // Helper function to fetch all agents
  const fetchAllAgents = async () => {
    try {
      const allAgents: Agent[] = [];
      const officeIds = selectedOffices.map(office => office.id);
      
      if (officeIds.length > 0) {
        const url = `${process.env.REACT_APP_API_URL}/agents/offices`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            office_ids: officeIds
          })
        });

        if (response.ok) {
          const data = await response.json();
          const agentList = Array.isArray(data) ? data : [];
          agentList.forEach((agent: any) => {
            allAgents.push({
              id: agent.id || agent._id,
              name: `${agent.firstName || ""} ${agent.lastName || ""}`.trim() || "(no name)"
            });
          });
        }
      } else {
        // Fallback: fetch brokerage-wide agents via users-search
        const brokerageId = (isTeammember && parentBrokerageId) ? parentBrokerageId : userId;
        if (brokerageId) {
          try {
            const params = new URLSearchParams({
              licenseType: "BROKERAGE_AGENT",
              brokerageFirmID: brokerageId,
              page: "1",
              limit: String(1000)
            });
            const url = `${process.env.REACT_APP_API_URL}/users-search?${params.toString()}`;
            const resp = await fetch(url, {
              headers: { Accept: "application/json", Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            if (resp.ok) {
              const json = await resp.json();
              const results = Array.isArray(json) ? json : json.results || [];
              results.forEach((u: any) => {
                allAgents.push({ id: String(u.id ?? u._id ?? ""), name: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || String(u.id ?? u._id) });
              });
            }
          } catch (e) {
            console.error("Failed to fetch brokerage agents:", e);
          }
        }
      }

      if (allAgents.length > 0) {
        setSelectedAgents(allAgents);
      }
    } catch (err) {
      console.error("Failed to fetch all agents:", err);
    }
  };

  const handleAgentChange = (opt: AgentOption | null) => {
    if (opt?.value) {
      if (opt.value === "__ALL__") {
        // Fetch and select all agents
        fetchAllAgents();
      } else {
        const agent = { id: opt.value, name: opt.label };
        if (!selectedAgents.some(a => a.id === agent.id)) {
          setSelectedAgents([...selectedAgents, agent]);
        }
      }
    }
    // Clear the select after adding
    setSelectedAgentOption(null);
  };

  const handleBrokerageAgentChange = (opt: BrokerageAgentOption | null) => {
    if (opt?.value) {
      const agent = { id: opt.value, name: opt.label };
      if (!selectedAgents.some(a => a.id === agent.id)) {
        setSelectedAgents([...selectedAgents, agent]);
      }
    }
    // Clear the select after adding
    setSelectedBrokerageAgentOption(null);
  };

  const removeOffice = (officeId: string) => {
    setSelectedOffices(selectedOffices.filter(o => o.id !== officeId));
  };

  const removeAgent = (agentId: string) => {
    setSelectedAgents(selectedAgents.filter(a => a.id !== agentId));
  };

  // Helper function to fetch all team members
  const fetchAllTeamMembers = async () => {
    try {
      const allTeamMembers: TeamMember[] = [];

      if (selectedOffices.length > 0) {
        const officeIds = selectedOffices.map((o) => o.id).filter((id) => id && id !== "__ALL__");
        if (officeIds.length > 0) {
          let page = 1;
          let hasMore = true;
          while (hasMore) {
            const { results, hasMore: more } = await fetchOfficeTeamMembersSelect2Page(officeIds, page, "");
            results.forEach((item: any) => {
              const id = item.id || item._id;
              if (!id) return;
              const name = String(
                item.text ?? item.name ?? `${item.firstName || ""} ${item.lastName || ""}`.trim()
              ).trim();
              if (!allTeamMembers.some((tm) => tm.id === id)) {
                allTeamMembers.push({
                  id,
                  name: name || "---",
                  firstName: item.firstName || "",
                  lastName: item.lastName || "",
                });
              }
            });
            hasMore = more;
            page++;
          }
        }
      } else {
        // Fallback to brokerage-wide team members
        const brokerageId = (isTeammember && parentBrokerageId) ? parentBrokerageId : userId;
        const brokerageIds = brokerageId ? [brokerageId] : [];
        if (brokerageIds.length > 0) {
          let page = 1;
          let hasMore = true;
          while (hasMore) {
            const params = new URLSearchParams({
              page: String(page),
              limit: String(PAGE_SIZE)
            });
            brokerageIds.forEach(id => params.append('brokerageFirmIDs[]', id));
            const url = `${process.env.REACT_APP_API_URL}/users/brokerage-team-members/by-brokerage-ids/select2?${params.toString()}`;
            const response = await fetch(url, { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } });
            if (!response.ok) break;
            const data = await response.json();
            const results = data.results || [];
            results.forEach((item: any) => {
              const id = item.id || item._id;
              if (!allTeamMembers.some(tm => tm.id === id)) {
                allTeamMembers.push({ id, name: item.name, firstName: "", lastName: "" });
              }
            });
            hasMore = data.pagination?.more || false;
            page++;
          }
        }
      }

      if (allTeamMembers.length > 0) {
        setSelectedTeamMembers(allTeamMembers);
      }
    } catch (err) {
      console.error("Failed to fetch all team members:", err);
    }
  };

  const handleTeamMemberChange = (opt: TeamMemberOption | null) => {
    if (opt?.value) {
      if (opt.value === "__ALL__") {
        // Fetch and select all team members
        fetchAllTeamMembers();
      } else {
        const teamMember = { id: opt.value, name: opt.label, firstName: "", lastName: "" };
        if (!selectedTeamMembers.some(tm => tm.id === teamMember.id)) {
          setSelectedTeamMembers([...selectedTeamMembers, teamMember]);
        }
      }
    }
    // Clear the select after adding
    setSelectedTeamMemberOption(null);
  };

  const removeTeamMember = (teamMemberId: string) => {
    setSelectedTeamMembers(selectedTeamMembers.filter(tm => tm.id !== teamMemberId));
  };

  // ===== API: Generate Report =====
  const generateReport = async () => {
    try {
      setGeneratingReport(true);

      const billToCategoryMap: Record<PayerScope, string> = {
        office: "office",
        agent: "agent",
        // group: "group",
      };

      const filters: any = {
        status: [],
        order_types: [],
        date_from: dateFrom ? `${dateFrom}T00:00:00Z` : undefined,
        date_to: dateTo ? `${dateTo}T23:59:59Z` : undefined,
        search: search.trim() || undefined,
        bill_to_category: billToCategoryMap[scope],
      };

      if (statusUnpaid) filters.status.push("Pending");
      if (statusPartial) filters.status.push("Partial Payment");
      if (statusOverdue) filters.status.push("Overdue");

      if (orderTypes.sales) filters.order_types.push("sales");
      if (orderTypes.service_install) filters.order_types.push("installation");
      if (orderTypes.removal) filters.order_types.push("removal");

      const selectedOrders = (selectedOrdersForReport || [])
        .map((o) => ({
          order_id: String(o.order_id || "").trim(),
          order_type: String(o.order_type || "sales").toLowerCase(),
        }))
        .filter((o) => o.order_id);
      if (selectedOrders.length > 0) {
        filters.selected_orders = selectedOrders;
      }

      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (
          value === undefined ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          delete filters[key];
        }
      });
 
      // Build bill_meta object
      const billToMembers: any[] = [];
 
      if (isAgent && userId && userName) {
        billToMembers.push({ id: userId, type: "agent", name: userName });
      }

      selectedAgents.forEach((agent) => {
        if (!billToMembers.some((m) => m.id === agent.id)) {
          billToMembers.push({ id: agent.id, type: "agent", name: agent.name });
        }
      });
      selectedTeamMembers.forEach((teamMember) =>
        billToMembers.push({ id: teamMember.id, type: "team_member", name: teamMember.name })
      );
      selectedOffices.forEach((office) =>
        billToMembers.push({ id: office.id, type: "office", name: office.label })
      );
      // if (scope === "group" && selectedGroup) {
      //   billToMembers.push({ id: selectedGroup.id, type: "group", name: selectedGroup.name });
      // }
 
      const billMeta = {
        bill_from_name: selectedVendor?.name || "Vendor",
        bill_to_name: userName || "",
        bill_to_members: billToMembers
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/batch-payments/buyer/reports`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            filters,
            bill_meta: billMeta
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate report");
      }

      const data = await response.json();
      console.log("Generated report data:", data);

      // Navigate to report page with the _id - dynamic based on login type
      if (isAgent) {
        navigate(`/agent/batchPaymentReport/${data._id}`);
      } else if (isTeammember) {
        navigate(`/teammember/batchPaymentReport/${data._id}`);
      } else {
        navigate(`/realtor/batchPaymentReport/${data._id}`);
      }
      
      showSuccess("Report generated successfully!");

    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  // Helper function to build filters
  const buildFilters = useCallback(() => {
    const filters: any = {
      payer_scope: scope,
      order_types: [],
      status: [],
      date_from: dateFrom ? `${dateFrom}T00:00:00Z` : undefined,
      date_to: dateTo ? `${dateTo}T23:59:59Z` : undefined,
      search: search.trim() || undefined
    };

    // Add vendor/signage context if selected
    if (selectedVendor?.supplier_id) {
      filters.signage_id = selectedVendor.supplier_id;
    }

    // For realtor or team member logins, include their brokerage ID as company_id
    if (!isAgent) {
      const effectiveCompanyId = (isTeammember && parentBrokerageId) ? parentBrokerageId : userId;
      if (effectiveCompanyId) {
        filters.company_id = effectiveCompanyId;
      }
    }

    // Add office IDs if offices are selected (regardless of scope)
    if (selectedOffices.length > 0) {
      filters.office_ids = selectedOffices.map(o => ({ id: o.id, name: o.label }));
    }

    // Add agent IDs if agents are selected (regardless of scope)
    if (selectedAgents.length > 0) {
      filters.agent_ids = selectedAgents.map(a => ({ id: a.id, name: a.name }));
    }

    // For agent login, always include the current user's ID in agent_ids
    if (isAgent && userId) {
      if (!filters.agent_ids) {
        filters.agent_ids = [];
      }
      if (!filters.agent_ids.some((agent: any) => agent.id === userId)) {
        filters.agent_ids.push({ id: userId, name: userName || "" });
      }
    }

    // Add team member IDs if team members are selected (regardless of scope)
    if (selectedTeamMembers.length > 0) {
      filters.team_member_ids = selectedTeamMembers.map(tm => ({ id: tm.id, name: tm.name }));
    }

    // Add group_id if group is selected
    // if (scope === "group" && selectedGroup?.id) {
    //   filters.group_id = selectedGroup.id;
    // }

    // Add order types
    if (orderTypes.sales) filters.order_types.push("sales");
    if (orderTypes.service_install) filters.order_types.push("installation");
    if (orderTypes.removal) filters.order_types.push("removal");

    // Add status filters
    if (statusUnpaid) filters.status.push("Pending");
    if (statusPartial) filters.status.push("Partial Payment");
    if (statusOverdue) filters.status.push("Overdue");

    const billToCategoryMap: Record<PayerScope, string> = {
      office: "office",
      agent: "agent",
      // group: "group",
    };
    filters.bill_to_category = billToCategoryMap[scope];

    // Remove undefined values and empty arrays
    Object.keys(filters).forEach(key => {
      if (filters[key] === undefined || (Array.isArray(filters[key]) && filters[key].length === 0)) {
        delete filters[key];
      }
    });

    return filters;
  }, [
    scope,
    selectedVendor,
    isAgent,
    isTeammember,
    parentBrokerageId,
    userId,
    selectedOffices,
    selectedAgents,
    selectedTeamMembers,
    // selectedGroup,
    userName,
    dateFrom,
    dateTo,
    search,
    orderTypes,
    statusUnpaid,
    statusPartial,
    statusOverdue
  ]);

  // ===== API: Get Orders List =====
  const fetchOrdersList = useCallback(async (overridePagination?: { pageIndex: number; pageSize: number }) => {
    try {
      setFetchingOrders(true);

      const filters = buildFilters();
      const paginationToUse = overridePagination || externalPagination || { pageIndex: 0, pageSize: 10 };
      const page = paginationToUse.pageIndex + 1;
      const limit = paginationToUse.pageSize;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/buyer-batch-payments/orders/datatable`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page,
            limit,
            sort_field: "order_date",
            sort_dir: -1,
            filters
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }

      const data = await response.json();
      console.log("Fetched orders data:", data);
      const orders = data?.data || [];
      const total = data?.kpis?.orders || 0;
      const totalPages = data?.totalPages || Math.ceil(total / limit);

      onOrdersFetched?.({
        orders,
        total,
        totalPages,
        page,
        limit
      });

      setOrdersFetched(true);

    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to fetch orders");
    } finally {
      setFetchingOrders(false);
    }
  }, [
    buildFilters,
    externalPagination,
    token,
    onOrdersFetched
  ]);

  // Ref to track previous pagination to detect changes
  const prevPaginationRef = useRef<{ pageIndex: number; pageSize: number } | null>(null);

  // Refetch orders when pagination changes (only if orders were already fetched)
  useEffect(() => {
    if (ordersFetched && externalPagination) {
      const prevPagination = prevPaginationRef.current;
      // Only refetch if pagination actually changed
      if (!prevPagination || 
          prevPagination.pageIndex !== externalPagination.pageIndex || 
          prevPagination.pageSize !== externalPagination.pageSize) {
        prevPaginationRef.current = externalPagination;
        fetchOrdersList();
      }
    }
  }, [externalPagination?.pageIndex, externalPagination?.pageSize, ordersFetched, fetchOrdersList]);

  // Reset pagination ref when orders are first fetched
  useEffect(() => {
    if (ordersFetched && externalPagination) {
      prevPaginationRef.current = externalPagination;
    }
  }, [ordersFetched]);

  const vendorOptions = useMemo(() => vendors?.map((v) => ({ value: v.supplier_id, label: v.name })), [vendors]);
  const officeOptions = useMemo(() => offices?.map((o) => ({ value: o.id, label: o.label })), [offices]);
  const agentOptions = useMemo(() => agents?.map((a) => ({ value: a.id, label: a.name })), [agents]);
  // const groupOptions = useMemo(() => groups?.map((g) => ({ value: g.id, label: g.name })), [groups]);
  const teamMemberOptions = useMemo(() => teamMembers.map((tm) => ({ value: tm.id, label: tm.name })), [teamMembers]);
  const dateFieldOptions = [
    { value: "Due Date", label: "Due Date" },
    { value: "Order Date", label: "Order Date" }
  ];

  return (
    <div className="card shadow-border-1 box-shadow-datatables p-2 bg-white mb-3">
      <div className="card-body">
        {/* Vendor Section */}
        <div className="row g-3 align-items-center mb-3">
          <div className="col-lg-3">
            <label className="col-form-label">Vendor</label>
            <Select
              classNamePrefix="rs"
              placeholder={loading ? "Loading vendors..." : "Select vendor..."}
              options={vendorOptions}
              value={selectedVendor ? { value: selectedVendor.supplier_id, label: selectedVendor.name } : null}
              onChange={(opt: any) => {
                const vendor = opt ? vendors.find(v => v.supplier_id === opt.value) || null : null;
                setSelectedVendor(vendor);
                onVendorChange?.(vendor);
              }}
              isClearable
              components={{ DropdownIndicator }}
              styles={customSelectStyles}
            />
          </div>
        </div>

        <div className="border-bottom pb-4 mb-4"></div>

        <div className="fw-semibold mb-3">Payer Scope</div>

        {/* Scope Radios - Only show for realtor/teammember */}
        {!isAgent && (
          <div className="d-flex flex-wrap gap-4 border-bottom pb-4">
            {["office", "agent"].map((k) => (
              <label key={k} className="form-check d-inline-flex align-items-center gap-2">
                <input type="radio" className="form-check-input" checked={scope === (k as PayerScope)} onChange={() => handleScopeChange(k as PayerScope)} />
                <span className="text-capitalize">{k}</span>
              </label>
            ))}
            {/* Group scope + customer-groups select — hidden
            {scope === "group" && (
              <div style={{ minWidth: 220 }}>
                <Select
                  classNamePrefix="rs"
                  placeholder={loading ? "Loading groups..." : "Select group..."}
                  options={groupOptions}
                  value={selectedGroup ? { value: selectedGroup.id, label: selectedGroup.name } : null}
                  onChange={(opt: any) => setSelectedGroup(opt ? { id: opt.value, name: opt.label } : null)}
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
              </div>
            )}
            */}
          </div>
        )}

        {/* Agent Display - Only show for agent login */}
        {isAgent && (
          <div className="d-flex flex-wrap gap-4 border-bottom pb-4">
            <label className="form-check d-inline-flex align-items-center gap-2">
              <input type="radio" className="form-check-input" checked={true} disabled />
              <span>Agent - {userName || "Unknown Agent"}</span>
            </label>
          </div>
        )}

        {/* Bill-To Category for Agent - Only show for agent login */}
        {isAgent && (
          <div className="row g-3 align-items-center mt-2 border-bottom pb-4">
            <div className="col-lg-2">
              <label className="col-form-label">Bill-To Category</label>
              <div className="form-check">
                <input id="billToCategoryAgent" className="form-check-input" type="checkbox" checked={true} disabled />
                <label htmlFor="billToCategoryAgent" className="form-check-label">
                  Agent-billed
                </label>
              </div>
            </div>
          </div>
        )}
        {/* Selected Vendor Display */}
        {userName && !isAgent && (
          <div className="row g-3 align-items-center mb-3">
            <div className="col-lg-3">
              <label className="col-form-label">Company</label> <br />
              <div className="vendor-chip">
                {isTeammember && parentBrokerageId ? parentBrokerageName : userName}
              </div>
            </div>
          </div>
        )}

        {/* Office - Only show for Office scope and not agent login */}
        {!isAgent && scope === "office" && (
          <>
            <div className="row mt-2">
              <div className="col-lg-3">
                <label className="col-form-label">Offices</label>
                <AsyncPaginate
                  key={(isTeammember && parentBrokerageId ? parentBrokerageId : userId) || 'no-user'}
                  classNamePrefix="rs"
                  placeholder="Add offices..."
                  isDisabled={!(isTeammember && parentBrokerageId ? parentBrokerageId : userId)}
                  loadOptions={loadOfficeOptions}
                  value={selectedOfficeOption}
                  onChange={handleOfficeChange}
                  additional={{ page: 1 }}
                  defaultOptions={true}
                  debounceTimeout={300}
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
                {/* Chips under the select */}
                {selectedOffices.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {selectedOffices.map((o) => (
                      <span key={o.id} className="chip badge rounded-pill fs-11 fw-medium">
                        {o.label}
                        <span className="ms-1 fs-16" onClick={() => removeOffice(o.id)}>×</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="row mt-2">
              <div className="col-lg-3">
                <div className="col-form-label">Team Members</div>
                <AsyncPaginate
                  key={selectedOffices.map(o => o.id).join(',') || 'no-offices'}
                  classNamePrefix="rs"
                  placeholder="Select team member..."
                  isDisabled={selectedOffices.length === 0}
                  loadOptions={loadTeamMemberOptions}
                  value={selectedTeamMemberOption}
                  onChange={handleTeamMemberChange}
                  additional={{ page: 1 }}
                  defaultOptions={true}
                  debounceTimeout={300}
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
                {/* Chips under the select */}
                {selectedTeamMembers.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {selectedTeamMembers.map((tm) => (
                      <span key={tm.id} className="chip badge rounded-pill fs-11 fw-medium">
                        {tm.name}
                        <span className="ms-1 fs-16" onClick={() => removeTeamMember(tm.id)}>×</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Agents selector hidden for Office scope by request. */}
            {false && (
            <div className="row mt-2">
              <div className="col-lg-3">
                <label className="col-form-label">Agents</label>
                <AsyncPaginate
                  key={selectedOffices.map(o => o.id).join(',') || 'no-offices'}
                  classNamePrefix="rs"
                  placeholder="Add agents..."
                  isDisabled={selectedOffices.length === 0}
                  loadOptions={loadOfficeAgentOptions}
                  value={selectedAgentOption}
                  onChange={handleAgentChange}
                  additional={{ page: 1 }}
                  defaultOptions={true}
                  debounceTimeout={300}
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
                {/* Chips under the select */}
                {selectedAgents.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {selectedAgents.map((a) => (
                      <span key={a.id} className="chip badge rounded-pill fs-11 fw-medium">
                        {a.name}
                        <span className="ms-1 fs-16" onClick={() => removeAgent(a.id)}>×</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}
          </>

        )}

        {/* Agent - Only show for Agent scope and not agent login */}
        {!isAgent && scope === "agent" && (
          <div className="row g-3 align-items-center mt-2">
            <div className="col-lg-3">
              <label className="col-form-label">Agents</label>
              <AsyncPaginate
                classNamePrefix="rs"
                placeholder="Search agents..."
                value={selectedBrokerageAgentOption}
                loadOptions={loadBrokerageAgentOptions}
                onChange={handleBrokerageAgentChange}
                additional={{ page: 1 }}
                defaultOptions
                debounceTimeout={300}
                isClearable
                components={{ DropdownIndicator }}
                styles={customSelectStyles}
              />
              {/* Chips under the select */}
              {selectedAgents.length > 0 && (
                <div className="d-flex gap-2 flex-wrap mt-2">
                  {selectedAgents.map((a) => (
                    <span key={a.id} className="chip badge rounded-pill fs-11 fw-medium">
                      {a.name}
                      <span className="ms-1 fs-16" onClick={() => removeAgent(a.id)}>×</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bill-To Category - Dynamic label based on scope, only for realtor/teammember */}
        {!isAgent && (
          <div className="row g-3 align-items-center mt-2 border-bottom pb-4">
            <div className="col-lg-2">
              <label className="col-form-label">Bill-To Category</label>
              <div className="form-check">
                <input id="billToCategory" className="form-check-input" type="checkbox" checked={billToCompany} onChange={(e) => setBillToCompany(e.target.checked)} disabled />
                <label htmlFor="billToCategory" className="form-check-label">
                  {scope === "office" && "Office-billed"}
                  {scope === "agent" && "Agent-billed"}
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Placed By / Types / Date Field */}
        <div className="row g-4 mt-2 border-bottom pb-4">
          {/* <div className="col-lg-4">
            <div className="mb-2 fw-semibold">Placed By</div>
            <label className="form-check d-flex align-items-center gap-2">
              <input type="checkbox" className="form-check-input" checked={placedBy.officeManager} onChange={(e) => setPlacedBy({ ...placedBy, officeManager: e.target.checked })} />
              Office Manager
            </label>
            <label className="form-check d-flex align-items-center gap-2 mt-2">
              <input type="checkbox" className="form-check-input" checked={placedBy.agent} onChange={(e) => setPlacedBy({ ...placedBy, agent: e.target.checked })} />
              Agent
            </label>
          </div> */}
          <div className="col-lg-4">
            <div className="mb-2 fw-semibold">Order Types</div>
            <label className="form-check d-flex align-items-center gap-2">
              <input type="checkbox" className="form-check-input" checked={orderTypes.sales} onChange={(e) => setOrderTypes({ ...orderTypes, sales: e.target.checked })} />
              Sales
            </label>
            <label className="form-check d-flex align-items-center gap-2 mt-2">
              <input type="checkbox" className="form-check-input" checked={orderTypes.service_install} onChange={(e) => setOrderTypes({ ...orderTypes, service_install: e.target.checked })} />
              Installation
            </label>
            <label className="form-check d-flex align-items-center gap-2 mt-2">
              <input type="checkbox" className="form-check-input" checked={orderTypes.removal} onChange={(e) => setOrderTypes({ ...orderTypes, removal: e.target.checked })} />
              Removal
            </label>
          </div>
          <div className="col-lg-4">
            <div className="mb-2 fw-semibold">Date Field</div>
            {/* <div className="col-lg-3">
              <Select
                classNamePrefix="rs"
                options={dateFieldOptions}
                value={dateFieldOptions.find(opt => opt.value === dateField)}
                onChange={(opt: any) => setDateField(opt?.value || "Due Date")}
                components={{ DropdownIndicator }}
                styles={customSelectStyles}
              />
            </div> */}
            <div className="d-flex gap-2 mt-3">
              <div className="">
                <div className="fw-medium mb-1">From</div>
                <input type="date" className="payer-form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="">
                <div className="fw-medium mb-1">To</div>
                <input type="date" className="payer-form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* Status + Actions */}
        <div className="mt-4 d-flex align-items-center gap-2 flex-wrap border-bottom pb-4">
          <div className="fw-semibold me-2">Status:</div>
          <button
            className={`btn btn-sm btn-unpaid ${statusUnpaid ? "active" : "outline"} fw-bolder br-8 fs-12`}
            onClick={() => setStatusUnpaid(!statusUnpaid)}
          >
            Unpaid {statusUnpaid ? "(ON)" : "(OFF)"}
          </button>
          <button
            className={`btn btn-sm btn-partial ${statusPartial ? "active" : "outline"} fw-bolder br-8 fs-12`}
            onClick={() => setStatusPartial(!statusPartial)}
          >
            Partially Paid {statusPartial ? "(ON)" : "(OFF)"}
          </button>
          <button
            className={`btn btn-sm btn-overdue ${statusOverdue ? "active" : "outline"} fw-bolder br-8 fs-12`}
            onClick={() => setStatusOverdue(!statusOverdue)}
          >
            Overdue {statusOverdue ? "(ON)" : "(OFF)"}
          </button>
        </div>
        <div className="row mt-4">
          <div className="col-12 col-lg-5">
            <div className="d-flex gap-3 align-items-center">
              <div className="search-container flex-grow-1">
                <div className="search-input-wrapper">
                  <Search size={16} className="search-icon" />
                  <input
                    className="payer-form-control"
                    placeholder="Search orders..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary table-addbutton btn-slim fw-bold"
                  onClick={() => {
                    // Reset pagination to first page when clicking "Get Orders List"
                    if (onPaginationReset) {
                      onPaginationReset();
                    }
                    // Fetch with page 0 explicitly
                    fetchOrdersList({ pageIndex: 0, pageSize: 10 });
                  }}
                  disabled={fetchingOrders}
                >
                  <Search size={16} className="me-2" />
                  {fetchingOrders ? "Loading..." : "Get Orders List"}
                </button>
                {ordersFetched && (
                  <button
                    className="btn success-btn btn-slim fw-bold"
                    onClick={generateReport}
                    disabled={generatingReport}
                  >
                    <FileText size={16} className="me-2" />
                    {generatingReport ? "Generating..." : "Generate Batch Invoice"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Vendor chip styling */
        .vendor-chip {
          background-color: #bedbff;
          color: #193cb8;
          border: 1px solid #bedbff;
          padding: 5px 15px;
          border-radius: 10px;
          font-weight: 500;
          display: inline-block;
        }

        /* Chip badges - gray by default, colored on hover */
        .chip{
            background:#f5f5f5;
            color:#333;
            border:1px solid #e0e0e0;
            padding:.35rem .6rem;
            cursor:default;
            transition:all 0.2s ease;
        }
        .chip:hover{
            background:#ffeaea;
            border-color:#ffd0d0;
        }

        /* Custom status buttons per spec */
        .btn-unpaid.active{background:#ff6900;color:#fff;border-color:#ff6900}
        .btn-unpaid.active:hover{background:#f54a00;border-color:#f54a00}
        .btn-unpaid.outline{background:transparent;color:#ff6900;border:1px solid #ff6900}
        .btn-unpaid.outline:hover{background:#ffe2e2}

        .btn-partial.active{background:#ad46ff;color:#fff;border-color:#ad46ff}
        .btn-partial.active:hover{background:#9810fa;border-color:#9810fa}
        .btn-partial.outline{background:transparent;color:#ad46ff;border:1px solid #ad46ff}
        .btn-partial.outline:hover{background:#f3e6ff}

         .btn-overdue.active{background:#f53298;color:#fff;border-color:#f472b6}
        .btn-overdue.active:hover{background:#d8006b;border-color:#ec4899}
        .btn-overdue.outline{background:transparent;color:#ec4899;border:1px solid #ec4899}
        .btn-overdue.outline:hover{background:#fce7f3}

        /* Keep select look tidy */
        .rs__control{min-height:44px;border-radius:8px}

        /* Payer form controls - same styling as ups-form-control */
        .payer-form-control {
          width: 100%; 
          height: 39px; 
          border-radius: 10px; 
          padding: 10px 12px;
          border-width: 2px; 
          border-style: solid; 
          border-color: #c9ced6; 
          background-color: #f5f6fa;
          outline: none; 
          transition: all .2s ease-in-out; 
          box-shadow: none; 
          color: #111827;
        }
        .payer-form-control:hover { 
          border-color: #9ca3af; 
          background-color: #fafafa; 
        }
        .payer-form-control:focus { 
          border-color: #9ca3af; 
          background-color: #fafafa; 
          box-shadow: none; 
        }
        .payer-form-control.is-invalid { 
          border-color: #dc3545; 
        }

        /* Slim search bar and buttons */
        .search-container {
            position: relative;
        }
        
        .search-input-wrapper {
            position: relative;
            display: flex;
            align-items: center;
        }
        
        .search-icon {
            position: absolute;
            left: 12px;
            color: #6c757d;
            z-index: 2;
        }
        
        /* Override payer-form-control for search input with icon */
        .search-input-wrapper .payer-form-control {
            padding-left: 40px;
        }
        
        .btn-slim {
            height: 40px;
            padding: 8px 16px;
            font-size: 14px;
            font-weight: 500;
            border-radius: 8px;
            display: flex;
            align-items: center;
            white-space: nowrap;
            transition: all 0.2s ease;
        }
              `}</style>
    </div>
  );
}

